/**
 * VergleichOffertKarte — one compared offer's KI card with inline broker edit (P2 Placement Slice 11,
 * Tab 4b). Staerken/Schwaechen (effective = broker copy else KI), Begruendung, the E4 warnflag, and an
 * inline editor for the two data-less sub-scores + the narrative.
 */

import { useState } from 'react';
import {
  OFFER_STATUS_LABELS,
  VERGLEICH_E4_HINWEIS,
  type ComparisonOfferEintrag,
  type ComparisonOfferPatch,
  type ComparisonReportEintrag,
} from '@brokly/shared';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { CarrierLogo } from './CarrierLogo';
import { VergleichAmpel } from './VergleichAmpel';
import { VergleichScoreBadge } from './VergleichScoreBadge';

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="list-disc pl-4 text-sm">
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}

export function VergleichOffertKarte({
  item,
  bericht,
  canEdit,
  onPatch,
  saving,
}: {
  item: ComparisonOfferEintrag;
  bericht: ComparisonReportEintrag;
  canEdit: boolean;
  onPatch: (patch: ComparisonOfferPatch) => void;
  saving: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [staerken, setStaerken] = useState(item.effektive_staerken.join('\n'));
  const [schwaechen, setSchwaechen] = useState(item.effektive_schwaechen.join('\n'));
  const [hinweis, setHinweis] = useState(item.broker_hinweis ?? '');
  const [service, setService] = useState(item.service_subscore !== null ? String(item.service_subscore) : '');
  const [individuell, setIndividuell] = useState(item.individuell_subscore !== null ? String(item.individuell_subscore) : '');

  const brokerCopy = item.staerken !== null || item.schwaechen !== null;
  const e4Labels = item.e4_rule_keys.map((k) => bericht.zeilen.find((z) => z.rule_key === k)?.label ?? k).join(', ');

  const toLines = (s: string): string[] => s.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);

  const save = (): void => {
    onPatch({
      offer_document_id: item.offer_document_id,
      staerken: toLines(staerken),
      schwaechen: toLines(schwaechen),
      broker_hinweis: hinweis.trim() === '' ? null : hinweis,
      service_subscore: service === '' ? null : Number(service),
      individuell_subscore: individuell === '' ? null : Number(individuell),
    });
    setEditing(false);
  };

  const uebernehmenKi = (): void => {
    onPatch({ offer_document_id: item.offer_document_id, staerken: null, schwaechen: null });
    setEditing(false);
  };

  return (
    <div className="rounded-lg border border-border p-3" data-testid={`vergleich-karte-${item.offer_document_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <CarrierLogo name={item.carrier_name} logoUrl={item.logo_url} size="md" />
        <span className="font-medium">{item.carrier_name}</span>
        <span className="text-xs text-muted-foreground">{item.submission_number}</span>
        <VergleichScoreBadge score={item.score_total} rang={item.rang} e4={item.e4_flag} />
        <VergleichAmpel ampel={item.ampel} size="sm" />
        <Badge variant="info">{OFFER_STATUS_LABELS.im_vergleich}</Badge>
        {brokerCopy ? <Badge variant="info">bearbeitet</Badge> : <Badge variant="neutral">KI-Vorschlag</Badge>}
      </div>

      {item.e4_flag && (
        <div role="note" className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900">
          {VERGLEICH_E4_HINWEIS} ({e4Labels})
        </div>
      )}

      {!editing && (
        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <div className="text-xs font-medium text-muted-foreground">Staerken</div>
            <BulletList items={item.effektive_staerken} />
          </div>
          <div>
            <div className="text-xs font-medium text-muted-foreground">Schwaechen</div>
            <BulletList items={item.effektive_schwaechen} />
          </div>
        </div>
      )}

      {!editing && item.ki_begruendung && <p className="mt-2 text-sm text-muted-foreground">{item.ki_begruendung}</p>}
      {!editing && item.broker_hinweis && <p className="mt-1 text-sm">{item.broker_hinweis}</p>}

      {!editing && canEdit && (
        <Button variant="outline" size="sm" className="mt-2" onClick={() => setEditing(true)} data-testid={`vergleich-karte-edit-${item.offer_document_id}`}>
          Bearbeiten
        </Button>
      )}

      {editing && (
        <div className="mt-2 flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Staerken (eine pro Zeile)</span>
            <textarea className="rounded-md border border-border p-2" rows={3} value={staerken} onChange={(e) => setStaerken(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Schwaechen (eine pro Zeile)</span>
            <textarea className="rounded-md border border-border p-2" rows={3} value={schwaechen} onChange={(e) => setSchwaechen(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Hinweis</span>
            <textarea className="rounded-md border border-border p-2" rows={2} value={hinweis} onChange={(e) => setHinweis(e.target.value)} />
          </label>
          <div className="flex gap-2">
            <label className="flex flex-1 flex-col gap-1 text-sm">
              <span className="text-muted-foreground">Service/Schadenabwicklung</span>
              <input type="number" min={0} max={100} placeholder="50 = neutral" className="rounded-md border border-border p-2" value={service} onChange={(e) => setService(e.target.value)} />
            </label>
            <label className="flex flex-1 flex-col gap-1 text-sm">
              <span className="text-muted-foreground">Individuelle Anforderungen</span>
              <input type="number" min={0} max={100} placeholder="50 = neutral" className="rounded-md border border-border p-2" value={individuell} onChange={(e) => setIndividuell(e.target.value)} />
            </label>
          </div>
          <div className="flex gap-2">
            <Button size="sm" disabled={saving} onClick={save} data-testid={`vergleich-karte-save-${item.offer_document_id}`}>Speichern</Button>
            <Button variant="ghost" size="sm" disabled={saving} onClick={uebernehmenKi}>KI-Text uebernehmen</Button>
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Abbrechen</Button>
          </div>
        </div>
      )}
    </div>
  );
}
